import React, { useState, useEffect } from 'react';
import { Modal, Button } from '@/components/common/Modal';
import { Input, TextArea, RadioGroup, Select } from '@/components/common/FormField';
import { transactionService } from '@/app/services/transactionService';
import { CreateTransactionRequest } from '@/app/types/transactions';
import { logger } from '@/app/utils/logger';

import './CreateTransactionModal.scss';

interface CreateTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (transactionId: string) => void;
  prefillUserId?: number;
  prefillOriginalTxId?: string;
  /** Pre-fill eligible_participant_id from the selected transaction row only */
  prefillEligibleParticipantId?: number | string | null;
  /** Pre-fill partner_product_id from source transaction/user */
  prefillPartnerProductId?: number | null;
}

// Admin-creatable reward types (excludes 'exploration' which is system-generated)
type AdminRewardType = 'easter_egg' | 'referral' | 'customer_support' | 'signup_bonus';

type TransactionMode = 'earn' | 'adjustment';
type AdjustmentType = 'add' | 'remove';

interface FormData {
  userId: string;
  epid: string;
  transactionMode: TransactionMode;
  // EARN fields
  rewardType: AdminRewardType;
  earnNotes: string;
  // ADJUSTMENT fields
  adjustmentType: AdjustmentType;
  adjustmentReason: string;
  originalTransactionId: string;
  // Common
  amount: string;
  internalNotes: string;
}

interface FormErrors {
  userId?: string;
  epid?: string;
  amount?: string;
  adjustmentReason?: string;
  originalTransactionId?: string;
}

const MIN_AMOUNT = 1;
const MAX_AMOUNT = 10000;
const MIN_REASON_LENGTH = 10;

const REWARD_TYPE_OPTIONS = [
  { value: 'easter_egg', label: 'Easter Egg' },
  { value: 'referral', label: 'Referral Bonus' },
  { value: 'customer_support', label: 'Customer Support / Goodwill' },
  { value: 'signup_bonus', label: 'Signup Bonus' },
];

const seedEpid = (prefill?: number | string | null): string => {
  if (prefill == null || prefill === '') {
    return '';
  }
  return String(prefill);
};

const getDefaultFormData = (
  prefillUserId?: number,
  prefillOriginalTxId?: string,
  prefillEligibleParticipantId?: number | string | null,
): FormData => ({
  userId: prefillUserId?.toString() || '',
  epid: seedEpid(prefillEligibleParticipantId),
  // Auto-switch to adjustment mode if original transaction ID is pre-filled
  transactionMode: prefillOriginalTxId ? 'adjustment' : 'earn',
  rewardType: 'customer_support',
  earnNotes: '',
  adjustmentType: 'add',
  adjustmentReason: '',
  originalTransactionId: prefillOriginalTxId || '',
  amount: '',
  internalNotes: '',
});

const isEpidRequired = (mode: TransactionMode, rewardType: AdminRewardType): boolean =>
  mode === 'adjustment' || (mode === 'earn' && rewardType !== 'signup_bonus');

export const CreateTransactionModal: React.FC<CreateTransactionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  prefillUserId,
  prefillOriginalTxId,
  prefillEligibleParticipantId,
  prefillPartnerProductId,
}) => {
  const [formData, setFormData] = useState<FormData>(() =>
    getDefaultFormData(prefillUserId, prefillOriginalTxId, prefillEligibleParticipantId)
  );
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData(
        getDefaultFormData(prefillUserId, prefillOriginalTxId, prefillEligibleParticipantId)
      );
      setErrors({});
    }
  }, [isOpen, prefillUserId, prefillOriginalTxId, prefillEligibleParticipantId]);

  const handleChange = <K extends keyof FormData>(key: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (errors[key as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [key]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    const userId = parseInt(formData.userId, 10);
    if (!formData.userId || isNaN(userId) || userId <= 0) {
      newErrors.userId = 'Valid user ID is required';
    }

    const amount = parseInt(formData.amount, 10);
    if (!formData.amount || isNaN(amount)) {
      newErrors.amount = 'Amount is required';
    } else if (amount < MIN_AMOUNT || amount > MAX_AMOUNT) {
      newErrors.amount = `Amount must be between ${MIN_AMOUNT} and ${MAX_AMOUNT}`;
    }

    const trimmedEpid = formData.epid.trim();
    if (trimmedEpid === '0') {
      newErrors.epid = 'Eligible participant ID cannot be 0';
    } else if (isEpidRequired(formData.transactionMode, formData.rewardType) && trimmedEpid.length === 0) {
      newErrors.epid = 'Eligible participant ID is required';
    }

    if (formData.transactionMode === 'adjustment') {
      if (!formData.adjustmentReason || formData.adjustmentReason.trim().length < MIN_REASON_LENGTH) {
        newErrors.adjustmentReason = `Reason must be at least ${MIN_REASON_LENGTH} characters`;
      }
      if (!formData.originalTransactionId || formData.originalTransactionId.trim().length === 0) {
        newErrors.originalTransactionId = 'Original transaction ID is required for adjustments';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSubmitting(true);
    try {
      const amount = parseInt(formData.amount, 10);
      const userId = parseInt(formData.userId, 10);
      const trimmedEpid = formData.epid.trim();
      const partnerProductId = prefillPartnerProductId ?? undefined;

      let request: CreateTransactionRequest;

      if (formData.transactionMode === 'earn') {
        request = {
          source_system: 'admin',
          community_ref_user_id: userId,
          partner_product_id: partnerProductId,
          transaction_type: 'earn',
          point_value: amount,
          transaction_datetime_utc: new Date().toISOString(),
          context_json: {
            reward_type: formData.rewardType,
            transaction_notes: formData.earnNotes.trim() || undefined,
            internal_notes: formData.internalNotes.trim() || undefined,
          },
        };
      } else {
        const pointValue = formData.adjustmentType === 'add' ? amount : -amount;
        request = {
          source_system: 'admin',
          community_ref_user_id: userId,
          partner_product_id: partnerProductId,
          transaction_type: 'adjustment',
          point_value: pointValue,
          transaction_datetime_utc: new Date().toISOString(),
          context_json: {
            transaction_notes: formData.adjustmentReason.trim(),
            original_transaction_id: formData.originalTransactionId.trim(),
            internal_notes: formData.internalNotes.trim() || undefined,
          },
        };
      }

      if (trimmedEpid.length > 0) {
        request.eligible_participant_id = trimmedEpid;
      }

      logger.info('Creating transaction', {
        userId,
        eligibleParticipantId: request.eligible_participant_id,
        partnerProductId: request.partner_product_id,
      });

      const response = await transactionService.createTransaction(request);
      onSuccess?.(response.transaction_id);
      onClose();
    } catch (error) {
      logger.error('Failed to create transaction', { error });
    } finally {
      setSubmitting(false);
    }
  };

  const isEarnMode = formData.transactionMode === 'earn';
  const epidRequired = isEpidRequired(formData.transactionMode, formData.rewardType);
  const showEpidInSummary =
    !isEarnMode || formData.rewardType !== 'signup_bonus' || formData.epid.trim().length > 0;
  const modalTitle = isEarnMode ? 'Award Points' : 'Create Adjustment';
  const submitLabel = isEarnMode ? 'Award Points' : 'Create Adjustment';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Creating...' : submitLabel}
          </Button>
        </>
      }
    >
      <div className="transaction-form">
        {/* Step 1: Transaction Type */}
        <div className="form-section">
          <h3 className="section-title">1. Transaction Type</h3>
          <RadioGroup
            name="transactionMode"
            value={formData.transactionMode}
            onChange={(val) => handleChange('transactionMode', val as TransactionMode)}
            options={[
              { value: 'earn', label: 'Award Points (Easter Egg, Referral, etc.)' },
              { value: 'adjustment', label: 'Adjustment (Refund, Correction)' },
            ]}
          />
        </div>

        {/* Step 2: User Selection */}
        <div className="form-section">
          <h3 className="section-title">2. Select User</h3>
          <div className="form-row">
            <Input
              label="User ID"
              value={formData.userId}
              onChange={(val) => handleChange('userId', val)}
              placeholder="Enter community_ref_user_id"
              type="number"
              required
              error={errors.userId}
            />
            <Input
              label="Eligible Participant ID"
              value={formData.epid}
              onChange={(val) => handleChange('epid', val)}
              placeholder="Numeric or tenant hash"
              type="text"
              required={epidRequired}
              error={errors.epid}
              hint="Numeric or tenant hash; intended ledger bucket."
            />
          </div>
        </div>

        {/* Step 3: Amount */}
        <div className="form-section">
          <h3 className="section-title">3. Point Amount</h3>
          <Input
            label="Amount"
            value={formData.amount}
            onChange={(val) => handleChange('amount', val)}
            placeholder={`Enter amount (${MIN_AMOUNT}-${MAX_AMOUNT})`}
            type="number"
            required
            error={errors.amount}
            hint={`Minimum: ${MIN_AMOUNT} pts, Maximum: ${MAX_AMOUNT} pts`}
          />
        </div>

        {/* EARN-specific fields */}
        {isEarnMode && (
          <>
            <div className="form-section">
              <h3 className="section-title">4. Reward Type</h3>
              <Select
                label="Type"
                value={formData.rewardType}
                onChange={(val) => handleChange('rewardType', val as AdminRewardType)}
                options={REWARD_TYPE_OPTIONS}
              />
            </div>

            <div className="form-section">
              <h3 className="section-title">5. Notes (Optional)</h3>
              <TextArea
                label="Transaction Notes"
                value={formData.earnNotes}
                onChange={(val) => handleChange('earnNotes', val)}
                placeholder="Optional notes about this transaction"
                maxLength={500}
                rows={2}
              />
            </div>

            <div className="form-section">
              <h3 className="section-title">6. Internal Notes (Optional)</h3>
              <TextArea
                label="Internal Notes"
                value={formData.internalNotes}
                onChange={(val) => handleChange('internalNotes', val)}
                placeholder="Internal notes (not shown to users)"
                maxLength={500}
                rows={2}
              />
            </div>
          </>
        )}

        {/* ADJUSTMENT-specific fields */}
        {!isEarnMode && (
          <>
            <div className="form-section">
              <h3 className="section-title">4. Adjustment Direction</h3>
              <RadioGroup
                name="adjustmentType"
                value={formData.adjustmentType}
                onChange={(val) => handleChange('adjustmentType', val as AdjustmentType)}
                options={[
                  { value: 'add', label: 'Add Points (refund/credit)' },
                  { value: 'remove', label: 'Remove Points (correction)' },
                ]}
              />
            </div>

            <div className="form-section">
              <h3 className="section-title">5. Original Transaction</h3>
              <Input
                label="Original Transaction ID"
                value={formData.originalTransactionId}
                onChange={(val) => handleChange('originalTransactionId', val)}
                placeholder="UUID of transaction being adjusted"
                required
                error={errors.originalTransactionId}
                hint="Required - the transaction being corrected or refunded"
              />
            </div>

            <div className="form-section">
              <h3 className="section-title">6. Reason</h3>
              <TextArea
                label="Adjustment Reason"
                value={formData.adjustmentReason}
                onChange={(val) => handleChange('adjustmentReason', val)}
                placeholder="Enter the reason for this adjustment"
                required
                error={errors.adjustmentReason}
                minLength={MIN_REASON_LENGTH}
                maxLength={500}
                rows={3}
              />
            </div>

            <div className="form-section">
              <h3 className="section-title">7. Internal Notes (Optional)</h3>
              <TextArea
                label="Internal Notes"
                value={formData.internalNotes}
                onChange={(val) => handleChange('internalNotes', val)}
                placeholder="Internal notes (not shown to users)"
                maxLength={500}
                rows={2}
              />
            </div>
          </>
        )}

        {/* Summary */}
        <div className="summary-section">
          <h3 className="section-title">Summary</h3>
          <div className="summary-content">
            <div className="summary-row">
              <span className="summary-label">Type:</span>
              <span className="summary-value">
                {isEarnMode
                  ? REWARD_TYPE_OPTIONS.find(o => o.value === formData.rewardType)?.label
                  : `Adjustment (${formData.adjustmentType === 'add' ? '+' : '-'})`
                }
              </span>
            </div>
            <div className="summary-row">
              <span className="summary-label">User ID:</span>
              <span className="summary-value">{formData.userId || '—'}</span>
            </div>
            {showEpidInSummary && (
              <div className="summary-row">
                <span className="summary-label">EPID:</span>
                <span className="summary-value">{formData.epid.trim() || '—'}</span>
              </div>
            )}
            <div className="summary-row">
              <span className="summary-label">Points:</span>
              <span className={`summary-value ${isEarnMode || formData.adjustmentType === 'add' ? 'positive' : 'negative'}`}>
                {isEarnMode || formData.adjustmentType === 'add' ? '+' : '-'}
                {formData.amount ? parseInt(formData.amount, 10).toLocaleString() : '0'} pts
              </span>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CreateTransactionModal;
